import {useState, useEffect} from 'preact/hooks';

import {ShopAppIcon} from '~/components/ShopAppIcon';
import type {Toast} from '~/types/toast';

const ANIMATION_DURATION = 300;
const TOAST_DEFAULT_DURATION = 5000;

interface ShopToastElementProps extends Toast {
  onClose: (id: string) => void;
}

const wrapperVariants = {
  default: 'bg-white',
  error: 'bg-poppy-l2',
};

const textVariants = {
  default: 'text-black',
  error: 'text-poppy-d1',
};

const buttonVariants = {
  default: 'bg-grayscale-l4 text-black',
  error: 'bg-poppy-d1 text-white',
};

export const ShopToastElement = (props: ShopToastElementProps) => {
  const {
    duration = TOAST_DEFAULT_DURATION,
    onClose,
    text,
    subText,
    actionText,
    handleAction,
    id,
    variant = 'default',
  } = props;

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    // Trigger the enter animation
    setVisible(true);

    // Set timeout for exit animation
    const showTimer = setTimeout(() => {
      if (duration !== 0) {
        setVisible(false);
        // Wait for the animation to finish before removing
        const removeTimer = setTimeout(() => {
          onClose(id);
        }, ANIMATION_DURATION);
        return () => clearTimeout(removeTimer);
      }
    }, duration);

    return () => clearTimeout(showTimer);
  }, [duration, id, onClose]);

  return (
    <div
      className={`z-max mx-auto flex w-85 transform items-center justify-start gap-x-3 rounded-md border-[0.5px] border-solid border-grayscale-l2l px-4 py-3 text-white shadow-lg transition-transform duration-${ANIMATION_DURATION} ease-in-out ${
        visible ? 'translate-y-0' : 'translate-y-94'
      } ${wrapperVariants[variant]} sm:bottom-0 sm:max-w-none sm:rounded-none`}
    >
      {variant !== 'error' && (
        <ShopAppIcon className="h-8 w-8 flex-shrink-0 text-purple-primary" />
      )}
      <div className="flex flex-col overflow-hidden">
        <div
          className={`font-sans text-body-title-small ${textVariants[variant]}`}
        >
          {text}
        </div>
        {subText && (
          <div
            className={`overflow-hidden text-ellipsis whitespace-nowrap font-sans text-caption ${textVariants[variant]}`}
          >
            {subText}
          </div>
        )}
      </div>
      {actionText && handleAction && (
        <button
          type="button"
          aria-label={actionText}
          className={`text-button ml-auto h-8 w-fit max-w-[40%] rounded-sm100 px-3 font-sans text-button-medium ${buttonVariants[variant]}`}
          data-testid="toast-action-button"
          onClick={handleAction}
        >
          {actionText}
        </button>
      )}
    </div>
  );
};
